import type { IAutocompleteAppellation } from "@bbj-types/appellations";
import type { ObjectId } from "@bbj-types/utils";
import {
  Autocomplete,
  type AutocompleteProps,
  Box,
  TextField,
  type Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { type ReactNode, useMemo } from "react";
import { AutocompleteAppellationsProvider } from "./context";
import { renderAutocompleteAppellationGroup } from "./group";
import { AutocompleteAppellationsList } from "./list";
import { AutocompleteAppellationsPaperSelectedOptions } from "./list/selectedOptions";
import { useAutocompleteAppellationsOptions } from "./logics/options";
import { useAutocompleteAppellationsSelected } from "./logics/selected";
import { renderAutocompleteAppellationOption } from "./option";

export type TAutocompleteAppellationsOption = IAutocompleteAppellation["matchedAppellations"][number] & {
  romeLabel: string;
  rome: string;
};

export interface IAutocompleteAppellationsProps<DisableClearable extends boolean | undefined = undefined>
  extends Omit<
    AutocompleteProps<TAutocompleteAppellationsOption, true, DisableClearable, false>,
    | "onInputChange"
    | "loading"
    | "loadingText"
    | "noOptionsText"
    | "multiple"
    | "options"
    | "filterOptions"
    | "getOptionKey"
    | "getOptionLabel"
    | "isOptionEqualToValue"
    | "groupBy"
    | "disableCloseOnSelect"
    | "renderInput"
    | "renderGroup"
    | "renderOption"
    | "renderValue"
  > {
  label?: ReactNode;
  value: Required<AutocompleteProps<TAutocompleteAppellationsOption, true, DisableClearable, false>>["value"];
  onChange: Required<AutocompleteProps<TAutocompleteAppellationsOption, true, DisableClearable, false>>["onChange"];
}

export const AutocompleteAppellations = <DisableClearable extends boolean | undefined>({
  label,
  value,
  onChange,
  ...props
}: IAutocompleteAppellationsProps<DisableClearable>) => {
  const valueSet = useMemo(() => new Set<ObjectId>(value.map((v) => v._id)), [value]);
  const { textFieldValue, options, loading, handleInputChange } = useAutocompleteAppellationsOptions();
  const { selectedOptionsGroups, handleAutocompleteClose, handleClickSelectedOptionGroup, handleClickSelectedOption } =
    useAutocompleteAppellationsSelected(value, valueSet, onChange);
  const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));

  return (
    <AutocompleteAppellationsProvider
      value={value}
      valueSet={valueSet}
      onChange={onChange}
      options={options}
      selectedOptionsGroups={selectedOptionsGroups}
      handleClickSelectedOptionGroup={handleClickSelectedOptionGroup}
      handleClickSelectedOption={handleClickSelectedOption}
    >
      <Autocomplete
        {...props}
        value={value}
        onChange={onChange}
        onInputChange={handleInputChange}
        onClose={handleAutocompleteClose}
        loading={loading}
        loadingText={
          <>
            <Typography variant="body1">Recherche de postes en cours...</Typography>
            <AutocompleteAppellationsPaperSelectedOptions />
          </>
        }
        noOptionsText={
          <>
            <Typography variant="body1">
              {textFieldValue.length >= 4
                ? "Pas de résultat trouvé."
                : "Pas de résultat. Saisissez au moins 4 caractères pour avoir des propositions de postes."}
            </Typography>
            <AutocompleteAppellationsPaperSelectedOptions />
          </>
        }
        multiple
        options={loading ? [] : options}
        filterOptions={(x) => x}
        getOptionKey={(option) => option._id}
        getOptionLabel={(option) => option.libelle}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        groupBy={(opt) => opt.rome}
        disableCloseOnSelect
        slotProps={{
          paper: { sx: { maxHeight: "40vh", overflow: "auto" } },
          listbox: {
            component: AutocompleteAppellationsList,
            ...{ sx: { p: 2 } },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <Box
                sx={{
                  pr: "12px",
                }}
              >
                {!isLg ? (
                  value.length === 0 ? (
                    "Sélectionnez une appellation ou un code ROME"
                  ) : (
                    <Typography variant="body1" color="textPrimary">
                      {value.length} sélectionné{value.length > 1 ? "s" : ""}
                    </Typography>
                  )
                ) : (
                  "Sélectionnez une appellation ou un code ROME"
                )}
              </Box>
            }
            {...(label && { label })}
          />
        )}
        renderGroup={renderAutocompleteAppellationGroup}
        renderOption={renderAutocompleteAppellationOption}
        renderValue={() => null}
      />
    </AutocompleteAppellationsProvider>
  );
};
